import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROTATION_MS = 5000;

const bannerSrc = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/')) return path;
  return `${API}/files/${path}`;
};

/**
 * Home banners carousel — image-only slides, no overlay, no text.
 *
 *   * 0 active banners → renders `null` (no empty gap on the page).
 *   * 1 banner         → static, no controls.
 *   * 2+ banners       → 5-second auto rotation + prev/next arrows + dots.
 *
 * Responsive behaviour:
 *   * Ratio 16:5 on md+ (identical to the legacy platform banner).
 *   * Ratio 21:9 on mobile so more of the image stays visible on narrow screens.
 *   * `object-fit: cover` + `object-position: center` — no letterboxing.
 */
export const HomeBannersCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/banners`)
      .then(r => setBanners(r.data || []))
      .catch(() => setBanners([]));
  }, []);

  const count = banners.length;
  const active = count > 0 ? banners[idx % count] : null;

  const next = useCallback(() => setIdx(i => (i + 1) % Math.max(count, 1)), [count]);
  const prev = useCallback(() => setIdx(i => (i - 1 + count) % Math.max(count, 1)), [count]);

  useEffect(() => {
    if (count < 2) return undefined;
    const t = setInterval(next, ROTATION_MS);
    return () => clearInterval(t);
  }, [count, next]);

  if (!active) return null;

  const inner = (
    <img
      src={bannerSrc(active.image_path)}
      alt="banner"
      className="w-full h-full object-contain object-center select-none"
      draggable={false}
    />
  );

  return (
    <section className="w-full bg-cream" data-testid="home-banners-carousel">
      <div className="max-w-6xl mx-auto px-0 sm:px-4 pt-3">
        {/* Universal ratio 1821 × 864 (~2.108:1) — the exact dimensions
            of the current default banner. All future banners MUST match
            this ratio. `object-contain` guarantees no crop; the cream
            background fills any letterbox gap so the banner blends
            seamlessly with the rest of the page. */}
        <div className="relative w-full aspect-[1821/864] overflow-hidden bg-cream">
          {active.link_url ? (
            (active.link_url.startsWith('http') ? (
              <a href={active.link_url} target="_blank" rel="noopener noreferrer" className="block w-full h-full" data-testid={`banner-slide-${active.banner_id}`}>
                {inner}
              </a>
            ) : (
              <Link to={active.link_url} className="block w-full h-full" data-testid={`banner-slide-${active.banner_id}`}>
                {inner}
              </Link>
            ))
          ) : (
            <div className="w-full h-full" data-testid={`banner-slide-${active.banner_id}`}>{inner}</div>
          )}

          {count > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Previous banner"
                className="absolute top-1/2 -translate-y-1/2 left-2 sm:left-3 h-9 w-9 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center transition"
                data-testid="banner-prev-btn"
              >
                <CaretLeft size={18} weight="bold" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Next banner"
                className="absolute top-1/2 -translate-y-1/2 right-2 sm:right-3 h-9 w-9 rounded-full bg-black/45 hover:bg-black/70 text-white flex items-center justify-center transition"
                data-testid="banner-next-btn"
              >
                <CaretRight size={18} weight="bold" />
              </button>
              <div className="absolute bottom-2 sm:bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="banner-dots">
                {banners.map((b, i) => (
                  <button
                    key={b.banner_id}
                    type="button"
                    onClick={() => setIdx(i)}
                    aria-label={`Banner ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-white' : 'w-1.5 bg-white/55 hover:bg-white/85'}`}
                    data-testid={`banner-dot-${i}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
